import { scannerMessages } from "constants/scanner.messages";
import { DriverLifecycleHooks } from "lib/deviceDrivers/driverLifecycle";
import {
  createSessionStorage,
  DeviceLike,
  getDeviceId,
} from "lib/deviceDrivers/sessionFramework/runtime/DriverSession";

import { type DriverActionResult } from "./adapters/driverEffects";
import { Xrp2iDriverSession } from "./runtime/Xrp2iDriverSession";

export const TT_XRP2I_DRIVER_NAME = "tt-xrp2i";
export const TT_XRP2I_CAPABILITIES = [scannerMessages.DEVICE_TIME];

const sessions = createSessionStorage(Xrp2iDriverSession);

/**
 * Synchronises the scanner hardware state before normal interaction resumes. The sync reads the device's current line-ending and EID output-format settings so the protocol parser can interpret live scan frames correctly.
 */
export function truTestXRP2ISyncState(deviceId: string): DriverActionResult {
  return sessions.getOrCreateSession(deviceId).syncState();
}

/**
 * Destroys the driver session for a device and removes it from the session cache. Each session holds a transport subscription, so it must be explicitly cancelled when the device disconnects.
 */
export function truTestXRP2IDestroySession(deviceId: string): void {
  const session = sessions.get(deviceId);
  if (!session) {
    return;
  }
  session.destroy();
  sessions.dispose(deviceId);
}

/**
 * Drives the periodic heartbeat for a connected device. The tick does nothing when no session exists, which covers the gap between a disconnect and the next time the scheduler fires.
 */
function truTestXRP2ITick(deviceId: string): DriverActionResult {
  const session = sessions.get(deviceId);
  if (!session) {
    return;
  }
  return session.tick();
}

/**
 * Routes a raw scanner payload into the active session for the matching device. The loose device type exists because the same physical scanner can arrive with either `deviceId` or `id` set, depending on the caller.
 */
export const truTestXRP2IProcessMessage = (
  device: DeviceLike,
  _type: string,
  _status: unknown,
  serializedPayload: string,
): DriverActionResult => {
  const deviceId = getDeviceId(device);
  if (!deviceId) {
    return;
  }
  return sessions.getOrCreateSession(deviceId).receive(serializedPayload);
};

/**
 * Queries the device time, then writes the corrected time. Warns the operator if the clock was significantly out before correction. The call is silently skipped when no session exists.
 */
function truTestXRP2ISetDeviceTime(deviceId: string): DriverActionResult {
  const session = sessions.get(deviceId);
  if (!session) {
    return;
  }
  return session.setDeviceTime();
}

export const TT_XRP2I_HANDLER = {
  [scannerMessages.DEVICE_TIME]: ({ deviceId }: { deviceId: string }) =>
    truTestXRP2ISetDeviceTime(deviceId),
};

function truTestXRP2IOnConnect(deviceId: string): DriverActionResult {
  const syncResult = truTestXRP2ISyncState(deviceId);
  const timeResult = truTestXRP2ISetDeviceTime(deviceId);
  if (!syncResult && !timeResult) {
    return;
  }
  if (!syncResult) {
    return timeResult;
  }
  if (!timeResult) {
    return syncResult;
  }
  return [...syncResult, ...timeResult];
}

export const TT_XRP2I_DRIVER_LIFECYCLE: DriverLifecycleHooks<DriverActionResult> =
  {
    onConnected: truTestXRP2IOnConnect,
    onDisconnected: truTestXRP2IDestroySession,
    onTick: truTestXRP2ITick,
    tickIntervalMs: 1000,
  };
